import { useState } from "react";
import { useCurrency } from "@/lib/currency";
import { Send } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  useCreateAnnouncement,
  usePublishAnnouncement,
  useConvertToHandshake,
} from "@/hooks/useAnnouncements";
import type { Announcement } from "@/services/announcementService";
import { fmtMoney } from "./helpers";



// ─── Create Announcement Dialog ───────────────────────────────────

export function CreateAnnouncementDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<"availability" | "restock">("availability");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [productName, setProductName] = useState("");
  const [unitPrice, setUnitPrice] = useState(""); // major units, the business's money
  const cur = useCurrency();
  const [availableQuantity, setAvailableQuantity] = useState("");

  const create = useCreateAnnouncement();
  const publish = usePublishAnnouncement();

  const reset = () => {
    setKind("availability");
    setTitle("");
    setBody("");
    setProductName("");
    setUnitPrice("");
    setAvailableQuantity("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (alsoPublish: boolean) => {
    create.mutate(
      {
        kind,
        title: title.trim(),
        body: body.trim() || undefined,
        productName: productName.trim() || undefined,
        unitPriceMinor: unitPrice
          ? Math.round((parseFloat(unitPrice) || 0) * 100)
          : undefined,
        availableQuantity: availableQuantity
          ? parseInt(availableQuantity, 10)
          : undefined,
      },
      {
        onSuccess: (announcement) => {
          if (alsoPublish) {
            publish.mutate(
              { id: announcement._id },
              {
                onSuccess: () => {
                  toast.success("Announcement created & published");
                  handleClose();
                },
                onError: (e: any) =>
                  toast.error(
                    e?.response?.data?.message || "Created but publish failed",
                  ),
              },
            );
          } else {
            toast.success("Announcement created as draft");
            handleClose();
          }
        },
        onError: (e: any) =>
          toast.error(e?.response?.data?.message || "Failed"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New announcement</DialogTitle>
          <DialogDescription>
            Broadcast stock availability or a restock notification to all
            connected distributors.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Kind *</Label>
            <Select
              value={kind}
              onValueChange={(v) => setKind(v as "availability" | "restock")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="availability">
                  Availability — "we have stock"
                </SelectItem>
                <SelectItem value="restock">
                  Restock — "fresh stock arrived"
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Coca-Cola 60cl in stock"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Body (optional)</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={2}
              placeholder="Details, terms, etc."
            />
          </div>

          <div className="space-y-1.5">
            <Label>Product name (optional)</Label>
            <Input
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="If a specific product"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ann-price">Unit price ({cur.symbol})</Label>
              <Input
                id="ann-price"
                type="number"
                min={0}
                step="0.01"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="6.00"
              />
              {unitPrice && (
                <p className="text-xs text-muted-foreground">
                  = {fmtMoney(Math.round((parseFloat(unitPrice) || 0) * 100))}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Available quantity</Label>
              <Input
                type="number"
                min={0}
                value={availableQuantity}
                onChange={(e) => setAvailableQuantity(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSubmit(false)}
            disabled={!title.trim() || create.isPending}
          >
            Save as draft
          </Button>
          <Button
            onClick={() => handleSubmit(true)}
            disabled={!title.trim() || create.isPending}
          >
            <Send className="h-4 w-4 mr-2" />
            {create.isPending ? "Creating..." : "Create & publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// ─── Convert Announcement to Handshake Dialog ─────────────────────

export function ConvertToHandshakeDialog({
  open,
  onClose,
  announcement,
}: {
  open: boolean;
  onClose: () => void;
  announcement: Announcement;
}) {
  const [quantity, setQuantity] = useState("1");
  const [note, setNote] = useState("");
  const [expectedDeliveryAt, setExpectedDeliveryAt] = useState("");

  const convert = useConvertToHandshake();

  const reset = () => {
    setQuantity("1");
    setNote("");
    setExpectedDeliveryAt("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = () => {
    convert.mutate(
      {
        id: announcement._id,
        input: {
          quantity: parseInt(quantity, 10),
          note: note.trim() || undefined,
          expectedDeliveryAt: expectedDeliveryAt || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Handshake request sent");
          handleClose();
        },
        onError: (e: any) =>
          toast.error(e?.response?.data?.message || "Failed"),
      },
    );
  };

  const total =
    (parseInt(quantity, 10) || 0) * (announcement.unitPriceMinor || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Convert to handshake</DialogTitle>
          <DialogDescription>
            Request goods from this announcement. The vendor will be notified.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Card className="p-3 bg-muted/30">
            <div className="font-medium">{announcement.title}</div>
            {announcement.unitPriceMinor != null && (
              <div className="text-sm text-muted-foreground">
                Unit: {fmtMoney(announcement.unitPriceMinor)}
              </div>
            )}
          </Card>

          <div className="space-y-1.5">
            <Label>Quantity *</Label>
            <Input
              type="number"
              min={1}
              max={announcement.availableQuantity ?? undefined}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
            {announcement.availableQuantity != null && (
              <p className="text-xs text-muted-foreground">
                Max available: {announcement.availableQuantity}
              </p>
            )}
          </div>

          {announcement.unitPriceMinor != null && (
            <div className="text-sm">
              Total: <strong className="font-medium">{fmtMoney(total)}</strong>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Expected delivery (optional)</Label>
            <Input
              type="date"
              value={expectedDeliveryAt}
              onChange={(e) => setExpectedDeliveryAt(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !quantity || parseInt(quantity, 10) < 1 || convert.isPending
            }
          >
            {convert.isPending ? "Sending..." : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

